import { Component, Inject, OnInit } from '@angular/core';
import { FileObject } from 'src/app/core/interfaces/file-object';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../core/components/base/base.component';
import {
  DISMISS_REASON_MAX,
  ExternalNoteForReview,
  noteFlagsLabel,
  noteFlagsTone,
  describeReferralFailure,
  reviewFileNotices,
  reviewGalleryFiles,
  reviewWarning,
} from '../../../../core/models/referral.model';
import { FeedbackService } from '../../../../core/services/feedback.service';
import { ReferralService } from '../../../../core/services/referral.service';

export interface ReferralNoteReviewDialogData {
  entryId: string;
  /** The practice whose inbox the row came from. */
  groupId: string;
}

export interface ReferralNoteReviewDialogResult {
  changed?: boolean;
}

/**
 * "Validate to timeline" (tasks/referrals.md §5.5, D22): the inbox opener for `referral-note` rows.
 *
 * Shows the external clinician's note exactly as written, who wrote it, and the referral it belongs to. Two decisions:
 * validate (it becomes an ordinary, read-only note on the patient's timeline) or dismiss with a reason. The author is
 * never told which (Q13).
 */
@Component({
  selector: 'app-referral-note-review-dialog',
  templateUrl: './referral-note-review-dialog.component.html',
  styleUrls: ['./referral-note-review-dialog.component.scss'],
})
export class ReferralNoteReviewDialogComponent extends BaseComponent implements OnInit {
  note: ExternalNoteForReview | null = null;
  isLoading = true;
  loadError: string | null = null;
  isSaving = false;
  actionError: string | null = null;
  dismissing = false;
  dismissReason = '';
  /** Stored files as gallery tiles, and one notice per file that has not arrived. Rebuilt on each load (see ngOnInit). */
  galleryFiles: FileObject[] = [];
  fileNotices: { id: string; name: string; text: string; blocking: boolean }[] = [];

  readonly flagsLabel = noteFlagsLabel;
  readonly flagsTone = noteFlagsTone;
  readonly dismissMax = DISMISS_REASON_MAX;

  constructor(
    private referralService: ReferralService,
    private dialogRef: MatDialogRef<ReferralNoteReviewDialogComponent, ReferralNoteReviewDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralNoteReviewDialogData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.referralService.getExternalNote(this.data.entryId, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (note) => {
        this.note = note;
        // Built ONCE per load, never from a getter the template binds: `mapMediaToFileObject` returns a new array of new
        // objects on every call, and FileGalleryComponent.ngOnChanges fires for every change-detection pass that is
        // handed one - so each tile would be rebuilt (and its image re-requested) every tick, and the object the image
        // dialog was opened with would be replaced underneath it. Same reason patient-document-list memoises its gallery.
        this.galleryFiles = reviewGalleryFiles(note.attachments);
        this.fileNotices = reviewFileNotices(note.attachments);
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.loadError = describeReferralFailure(error, 'This note could not be loaded.');
      },
    });
  }

  /** §16 R4: files that ended without arriving and can be asked for again (validators only, while the note awaits review). */
  get canRequestFilesAgain(): boolean {
    return !!this.note?.canReview && (this.note.attachments || []).some((a) => a.canRequestAgain);
  }

  requestFilesAgain(): void {
    if (!this.note || this.isSaving) return;
    this.isSaving = true;
    this.actionError = null;
    this.referralService.requestExternalNoteFilesAgain(this.note.id, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.isSaving = false;
        this.showToastMessage(`${result.requested} ${result.requested === 1 ? 'file is' : 'files are'} being requested again. Validation waits for them.`);
        this.ngOnInit();
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The files could not be requested again. Please try again.');
      },
    });
  }

  /** Validation files the note's media, so it waits until every file has arrived. */
  get filesPending(): boolean {
    return this.fileNotices.some((n) => n.blocking);
  }

  get warning(): string | null {
    return this.note ? reviewWarning(this.note) : null;
  }

  get patientLabel(): string {
    const p = this.note?.patient;
    if (!p) return '';
    return [[p.name, p.surname].filter(Boolean).join(' '), p.patientNumber ? `(${p.patientNumber})` : ''].filter(Boolean).join(' ');
  }

  async validate(): Promise<void> {
    if (!this.note || this.isSaving) return;
    if (this.note.isMortality) {
      const confirmed = await this.showConfirmationDialog('VALIDATE MORTALITY NOTE', this.warning || '', undefined, 'Cancel', 'Validate');
      if (!confirmed) return;
    }
    this.isSaving = true;
    this.actionError = null;
    this.referralService.validateExternalNote(this.note.id, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.showToastMessage(result.alreadyFiled ? 'This note was already on the timeline.' : 'Note added to the patient\'s timeline.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The note could not be validated. Please try again.');
      },
    });
  }

  confirmDismiss(): void {
    if (!this.note || this.isSaving) return;
    const reason = this.dismissReason.trim();
    if (!reason) {
      this.actionError = 'Enter a reason for dismissing this note.';
      return;
    }
    this.isSaving = true;
    this.actionError = null;
    this.referralService.dismissExternalNote(this.note.id, reason, this.data.groupId).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.showToastMessage('Note dismissed. It will not be added to the record.');
        this.dialogRef.close({ changed: true });
      },
      error: (error) => {
        this.isSaving = false;
        this.actionError = describeReferralFailure(error, 'The note could not be dismissed. Please try again.');
      },
    });
  }

  close(): void {
    this.dialogRef.close({ changed: false });
  }
}
